import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
  IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Snackbar,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as AcceptIcon,
  Cancel as DenyIcon,
  Inventory as InventoryIcon,
  LocalShipping as TruckIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ShoppingCart as OrderIcon,
  Store as StoreIcon,
  SwapHoriz as TransferIcon,
  Sync as SyncIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { inventoryApi, partsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface WhQty { warehouse: string; qty: number; }
interface SummaryItem {
  id: number; part_number: string; name: string; description?: string;
  category?: string; unit_cost: number; unit_price: number;
  unit_of_measure?: string; in_stock: number; reorder_point: number;
  warehouses: WhQty[];
}
interface Warehouse {
  warehouse: string; is_truck: boolean; is_main: boolean;
  technician?: string; is_mine: boolean; items: number;
  total_qty: number; negative_items: number;
}
interface Transfer {
  id: number; part_number: string; description?: string; qty: number;
  from_warehouse: string; to_warehouse: string; status: string;
  requested_by?: string; acted_by?: string; note?: string;
  erp_stock_entry?: string; created_at?: string; acted_at?: string;
}

const MANAGE_ROLES = ['SuperAdmin', 'Admin', 'Dispatcher'];
const transferStatusColor: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  requested: 'warning', accepted: 'success', denied: 'error', ordered: 'info', moved: 'success',
};

export default function InventoryPage() {
  const { user } = useAuth() as any;
  const isManager = (user?.roles || []).some((r: any) => MANAGE_ROLES.includes(typeof r === 'string' ? r : r?.name));

  const [items, setItems] = useState<SummaryItem[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [moveMode, setMoveMode] = useState(false);
  const [newTransfer, setNewTransfer] = useState({ part_number: '', qty: 1, from_warehouse: '', to_warehouse: '', note: '' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPart, setNewPart] = useState({ part_number: '', name: '', category: '', unit_cost: 0, unit_price: 0, reorder_point: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, w, t] = await Promise.all([
        inventoryApi.summary(), inventoryApi.warehouses(), inventoryApi.transfers(),
      ]);
      setItems(s.data); setWarehouses(w.data); setTransfers(t.data);
    } catch {
      setToast({ msg: 'Failed to load inventory', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const myWarehouse = useMemo(() => warehouses.find(w => w.is_mine)?.warehouse, [warehouses]);
  const mainWarehouse = useMemo(() => warehouses.find(w => w.is_main)?.warehouse || 'Stores - CC', [warehouses]);
  const categories = useMemo(() => ['all', ...new Set(items.map(i => i.category).filter(Boolean) as string[])], [items]);

  const filteredItems = items.filter(item => {
    const q = searchQuery.toLowerCase();
    const matches = item.name.toLowerCase().includes(q) || item.part_number.toLowerCase().includes(q);
    return matches && (categoryFilter === 'all' || item.category === categoryFilter);
  });

  const totalValue = items.reduce((s, i) => s + Math.max(i.in_stock, 0) * i.unit_cost, 0);
  const lowStock = items.filter(i => i.in_stock > 0 && i.reorder_point > 0 && i.in_stock <= i.reorder_point).length;
  const negativeCount = items.filter(i => i.in_stock < 0 || i.warehouses.some(w => w.qty < 0)).length;
  const pendingTransfers = transfers.filter(t => t.status === 'requested').length;
  const trucks = warehouses.filter(w => w.is_truck && (w.technician || w.items > 0));

  const canAct = (t: Transfer) =>
    t.status === 'requested' && (isManager || (myWarehouse && t.from_warehouse === myWarehouse));

  const doAction = async (t: Transfer, action: string) => {
    try {
      await inventoryApi.actOnTransfer(t.id, action);
      setToast({ msg: `Transfer #${t.id} ${action === 'accept' ? 'accepted — stock moved in ERP' : action + 'ed'}`, sev: 'success' });
      load();
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.detail || `Failed to ${action} transfer`, sev: 'error' });
    }
  };

  const submitTransfer = async () => {
    try {
      if (moveMode) {
        await inventoryApi.moveStock({
          part_number: newTransfer.part_number, qty: Number(newTransfer.qty),
          from_warehouse: newTransfer.from_warehouse,
          to_warehouse: newTransfer.to_warehouse || myWarehouse || '',
          note: newTransfer.note || undefined,
        });
        setToast({ msg: 'Stock moved — ERP transfer submitted', sev: 'success' });
      } else {
        await inventoryApi.createTransfer({
          part_number: newTransfer.part_number, qty: Number(newTransfer.qty),
          from_warehouse: newTransfer.from_warehouse,
          to_warehouse: newTransfer.to_warehouse || undefined,
          note: newTransfer.note || undefined,
        });
        setToast({ msg: 'Transfer requested', sev: 'success' });
      }
      setTransferDialogOpen(false);
      setNewTransfer({ part_number: '', qty: 1, from_warehouse: '', to_warehouse: '', note: '' });
      setTabValue(2); load();
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.detail || 'Failed', sev: 'error' });
    }
  };

  const submitNewPart = async () => {
    try {
      await partsApi.create(newPart);
      setAddDialogOpen(false);
      setNewPart({ part_number: '', name: '', category: '', unit_cost: 0, unit_price: 0, reorder_point: 0 });
      setToast({ msg: 'Part added — it will appear in ERP within one sync cycle', sev: 'success' });
      load();
    } catch (e: any) {
      setToast({ msg: e?.response?.data?.detail || 'Failed to add part', sev: 'error' });
    }
  };

  const whShort = (w: string) => w.replace(/ - CC$/, '').replace(/^Truck - /, '🚚 ');

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, background: 'linear-gradient(180deg, #1E4A6B, #0D2233)', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Inventory Management</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              <SyncIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Synced automatically with ERP (trucks are ERP warehouses)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isManager && (
              <Button variant="contained" startIcon={<TruckIcon />} onClick={() => { setMoveMode(true); setTransferDialogOpen(true); }} sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                Move Stock
              </Button>
            )}
            <Button variant="contained" startIcon={<TransferIcon />} onClick={() => { setMoveMode(false); setTransferDialogOpen(true); }} sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              Request Transfer
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)} sx={{ bgcolor: 'white', color: '#14548C', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>
              Add Item
            </Button>
            <IconButton sx={{ color: 'white' }} onClick={load}><RefreshIcon /></IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[{
          icon: <InventoryIcon />, color: '#14548C', value: items.length, label: 'Catalog Items',
        }, {
          icon: <>$</>, color: '#4caf50', value: `$${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, label: 'Stock Value (cost)',
        }, {
          icon: <TrendingDownIcon />, color: '#ff9800', value: lowStock, label: 'Low Stock',
        }, {
          icon: <WarningIcon />, color: '#f44336', value: negativeCount, label: 'Negative — Needs Correction',
        }].map((c, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: c.color, width: 48, height: 48 }}>{c.icon}</Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{c.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<StoreIcon />} iconPosition="start" label="All Inventory" />
          <Tab icon={<TruckIcon />} iconPosition="start" label={`Truck Stock (${trucks.length})`} />
          <Tab icon={<TransferIcon />} iconPosition="start" label={`Transfers${pendingTransfers ? ` (${pendingTransfers})` : ''}`} />
        </Tabs>

        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : tabValue === 0 ? (
          <Box>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Search parts…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 240 }} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Category</InputLabel>
                <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)}>
                  {categories.map(c => <MenuItem key={c} value={c}>{c === 'all' ? 'All categories' : c}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part #</TableCell><TableCell>Name</TableCell><TableCell>Category</TableCell>
                    <TableCell align="right">Price</TableCell><TableCell align="right">Total Stock</TableCell>
                    <TableCell>By Location (ERP)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.slice(0, 250).map(item => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{item.part_number}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell><Chip size="small" label={item.category || '—'} /></TableCell>
                      <TableCell align="right">${item.unit_price.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={item.in_stock}
                          color={item.in_stock < 0 ? 'error' : item.in_stock === 0 ? 'default' : (item.reorder_point > 0 && item.in_stock <= item.reorder_point) ? 'warning' : 'success'} />
                      </TableCell>
                      <TableCell>
                        {item.warehouses.length === 0 ? <Typography variant="caption" color="text.secondary">no stock records</Typography> :
                          item.warehouses.map(w => (
                            <Tooltip key={w.warehouse} title={w.qty < 0 ? `${w.warehouse}: negative — manager must correct stock in ERP` : w.warehouse}>
                              <Chip size="small" variant="outlined" color={w.qty < 0 ? 'error' : 'default'}
                                label={`${whShort(w.warehouse)}: ${w.qty}`} sx={{ mr: 0.5, mb: 0.5 }} />
                            </Tooltip>
                          ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredItems.length > 250 && (
              <Alert severity="info" sx={{ m: 2 }}>Showing first 250 of {filteredItems.length} items — refine the search to narrow down.</Alert>
            )}
          </Box>
        ) : tabValue === 1 ? (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {trucks.length === 0 && <Grid item xs={12}><Alert severity="info">No truck warehouses yet — they are created automatically for active technicians on the next ERP sync.</Alert></Grid>}
            {trucks.map(truck => {
              const truckItems = items
                .map(i => ({ part: i, wq: i.warehouses.find(w => w.warehouse === truck.warehouse) }))
                .filter(x => x.wq && x.wq.qty !== 0);
              return (
                <Grid item xs={12} sm={6} md={4} key={truck.warehouse}>
                  <Card variant="outlined" sx={{ borderColor: truck.is_mine ? '#14548C' : 'divider', height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar sx={{ bgcolor: '#14548C' }}><TruckIcon /></Avatar>
                        <Box>
                          <Typography fontWeight={600}>{whShort(truck.warehouse)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {truck.technician || 'unassigned'}{truck.is_mine ? ' · your truck' : ''} · ERP warehouse
                          </Typography>
                        </Box>
                      </Box>
                      {truckItems.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Empty</Typography>
                      ) : truckItems.map(({ part, wq }) => (
                        <Box key={part.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                          <Typography variant="body2">{part.name}</Typography>
                          <Chip size="small" color={wq!.qty < 0 ? 'error' : 'default'} label={wq!.qty} />
                        </Box>
                      ))}
                      {truck.negative_items > 0 && (
                        <Alert severity="error" sx={{ mt: 1, py: 0 }} icon={<WarningIcon fontSize="small" />}>
                          Negative stock — manager must correct levels in ERP
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell><TableCell>Part</TableCell><TableCell align="right">Qty</TableCell>
                  <TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Requested By</TableCell>
                  <TableCell>Status</TableCell><TableCell>ERP Entry</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.length === 0 && <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No transfers yet. Techs can request stock from another truck or the main warehouse — every request is recorded here.</Typography></TableCell></TableRow>}
                {transfers.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.id}</TableCell>
                    <TableCell><Typography variant="body2">{t.description || t.part_number}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{t.part_number}</Typography></TableCell>
                    <TableCell align="right">{t.qty}</TableCell>
                    <TableCell>{whShort(t.from_warehouse)}</TableCell>
                    <TableCell>{whShort(t.to_warehouse)}</TableCell>
                    <TableCell>{t.requested_by}<br /><Typography variant="caption" color="text.secondary">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={t.status} color={transferStatusColor[t.status] || 'default'} />
                      {t.acted_by && <Typography variant="caption" display="block" color="text.secondary">by {t.acted_by}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{t.erp_stock_entry || '—'}</Typography></TableCell>
                    <TableCell align="right">
                      {canAct(t) && (
                        <>
                          <Tooltip title="Accept — moves stock in ERP now">
                            <IconButton size="small" color="success" onClick={() => doAction(t, 'accept')}><AcceptIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Deny request">
                            <IconButton size="small" color="error" onClick={() => doAction(t, 'deny')}><DenyIcon /></IconButton>
                          </Tooltip>
                          {t.from_warehouse === mainWarehouse && isManager && (
                            <Tooltip title="Order — main warehouse will purchase instead">
                              <IconButton size="small" color="info" onClick={() => doAction(t, 'order')}><OrderIcon /></IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Request Transfer dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{moveMode ? 'Move Stock (immediate)' : 'Request Stock Transfer'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
          <Autocomplete size="small" options={items} getOptionLabel={i => `${i.name} (${i.part_number})`}
            value={items.find(i => i.part_number === newTransfer.part_number) || null}
            onChange={(_, v) => setNewTransfer({ ...newTransfer, part_number: v?.part_number || '' })}
            renderInput={params => <TextField {...params} label="Part" placeholder="Search the ERP catalog…" />} />
          <TextField size="small" type="number" label="Quantity" value={newTransfer.qty} inputProps={{ min: 1 }}
            onChange={e => setNewTransfer({ ...newTransfer, qty: Number(e.target.value) })} />
          <FormControl fullWidth size="small">
            <InputLabel>From (source location)</InputLabel>
            <Select value={newTransfer.from_warehouse} label="From (source location)" onChange={e => setNewTransfer({ ...newTransfer, from_warehouse: e.target.value })}>
              {warehouses.map(w => <MenuItem key={w.warehouse} value={w.warehouse}>{whShort(w.warehouse)}{w.technician ? ` — ${w.technician}` : ''}{w.is_main ? ' (main warehouse)' : ''}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>To (destination)</InputLabel>
            <Select value={newTransfer.to_warehouse || myWarehouse || ''} label="To (destination)" onChange={e => setNewTransfer({ ...newTransfer, to_warehouse: e.target.value })}>
              {warehouses.map(w => <MenuItem key={w.warehouse} value={w.warehouse}>{whShort(w.warehouse)}{w.is_mine ? ' (your truck)' : ''}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Note (optional)" value={newTransfer.note} onChange={e => setNewTransfer({ ...newTransfer, note: e.target.value })} multiline rows={2} />
          <Alert severity="info" sx={{ py: 0 }}>{moveMode ? 'Moves stock in ERP immediately and records it in the transfer log.' : "The source location can accept (stock moves in ERP), deny, or — if it's the main warehouse — order the item. Every request is recorded."}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newTransfer.part_number || !newTransfer.from_warehouse || newTransfer.qty <= 0}
            onClick={() => { if (!newTransfer.to_warehouse && myWarehouse) setNewTransfer(n => ({ ...n, to_warehouse: myWarehouse })); submitTransfer(); }}>
            {moveMode ? 'Move' : 'Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Item dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Catalog Item</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}>
          <TextField size="small" label="Part Number (SKU)" value={newPart.part_number} onChange={e => setNewPart({ ...newPart, part_number: e.target.value })} />
          <TextField size="small" label="Name" value={newPart.name} onChange={e => setNewPart({ ...newPart, name: e.target.value })} />
          <TextField size="small" label="Category" value={newPart.category} onChange={e => setNewPart({ ...newPart, category: e.target.value })} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField size="small" type="number" label="Unit Cost" value={newPart.unit_cost} onChange={e => setNewPart({ ...newPart, unit_cost: Number(e.target.value) })} />
            <TextField size="small" type="number" label="Unit Price" value={newPart.unit_price} onChange={e => setNewPart({ ...newPart, unit_price: Number(e.target.value) })} />
            <TextField size="small" type="number" label="Reorder Point" value={newPart.reorder_point} onChange={e => setNewPart({ ...newPart, reorder_point: Number(e.target.value) })} />
          </Box>
          <Alert severity="info" sx={{ py: 0 }}>New items are pushed to ERP automatically by the inventory sync.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newPart.part_number || !newPart.name} onClick={submitNewPart}>Add</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity={toast?.sev || 'success'} onClose={() => setToast(null)}>{toast?.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
